import { Injectable } from '@angular/core';
import { IFilterFacet, IFilterResponse } from '@impact/data';
import { TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs/operators';

import { FILTER_FACET, FILTER_SORTING, IParamsOfArrays } from '../../filter-service/filter.models';
import { FilterService } from '../../filter-service/filter.service';
import { BehaviorSubject, combineLatest } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class FilterPageService {
    public showLeasingPrices$ = new BehaviorSubject<boolean | undefined | null>(undefined);

    constructor(private translateService: TranslateService, private filterService: FilterService) { }

    private readonly translations = [
        'filterpage.sorting____created_date_ascending',
        'filterpage.sorting____created_date_descending',
        'filterpage.sorting____make_ascending',
        'filterpage.sorting____make_descending',
        'filterpage.sorting____model_ascending',
        'filterpage.sorting____model_descending',
        'filterpage.sorting____price_ascending',
        'filterpage.sorting____price_descending',
    ];

    private readonly translationMapper = (translations: Record<string, string>) => [
        {
            displayName: translations['filterpage.sorting____created_date_ascending'],
            value: FILTER_SORTING.CREATEDDATE_ASCENDING,
        },
        {
            displayName: translations['filterpage.sorting____created_date_descending'],
            value: FILTER_SORTING.CREATEDDATE_DESCENDING,
        },
        {
            displayName: translations['filterpage.sorting____make_ascending'],
            value: FILTER_SORTING.MAKE_ASCENDING,
        },
        {
            displayName: translations['filterpage.sorting____make_descending'],
            value: FILTER_SORTING.MAKE_DESCENDING,
        },
        {
            displayName: translations['filterpage.sorting____model_ascending'],
            value: FILTER_SORTING.MODEL_ASCENDING,
        },
        {
            displayName: translations['filterpage.sorting____model_descending'],
            value: FILTER_SORTING.MODEL_DESCENDING,
        },
        {
            displayName: translations['filterpage.sorting____price_ascending'],
            value: FILTER_SORTING.PRICE_ASCENDING,
        },
        {
            displayName: translations['filterpage.sorting____price_descending'],
            value: FILTER_SORTING.PRICE_DESCENDING,
        },
    ]

    get sortingOptions$() {
        return this.translateService.get(this.translations).pipe(map((translations) =>  this.translationMapper(translations)));
    }

    get facets$() {
        return combineLatest([this.filterService.filterData, this.filterService.initialFilterParams])
            .pipe(
                filter(([filterData]) => this.isNotNullOrUndefined(filterData)),
                map(([filterData, params]) => {
                    if (!filterData?.facets) return {};

                    // Sets the state of showing leasing/buying prices in product item
                    if (filterData?.facets['purchaseOption.keyword'] && filterData?.facets['purchaseOption.keyword'].items) {
                        if (filterData?.facets['purchaseOption.keyword'].items[0] && filterData?.facets['purchaseOption.keyword'].items[0].key === 'Leasing' && filterData?.facets['purchaseOption.keyword'].items[0].selected !== null) {
                            this.showLeasingPrices$.next(filterData?.facets['purchaseOption.keyword'].items[0].selected);
                        } else {
                            this.showLeasingPrices$.next(false);
                        }
                    } else {
                        this.showLeasingPrices$.next(false);
                    }

                    this.discardInitialParamsFromFilterdata(params, filterData);

                    const { isNew, isVan, ...unsortedItems } = filterData.facets;

                    this.setBooleanFilterToTrueIfExists(isNew);

                    this.setBooleanFilterToTrueIfExists(isVan);

                    const showOnly = this.getIsNewOrIsVanIfAnyOfThemIsPresent(isNew, isVan);

                    let purchaseOptionsItems: any;

                    if (filterData.facets['purchaseOption.keyword'] && filterData.facets['purchaseOption.keyword'].items && filterData.facets['purchaseOption.keyword'].items.length) {
                        if (filterData.facets['purchaseOption.keyword'].items) {
                            purchaseOptionsItems = filterData.facets['purchaseOption.keyword'];
                        }
                    }

                    const sortedFacets = this.sortFacets(unsortedItems);

                    return {
                        purchaseOptions: purchaseOptionsItems,
                        showOnly,
                        sorted: sortedFacets.sorted,
                        unsorted: sortedFacets.unsorted,
                    };
                })
            );
    }

    private isNotNullOrUndefined(filterData: IFilterResponse | null): boolean {
        return !!filterData;
    }

    private sortFacets(unsortedFacets: Record<string, IFilterFacet>) {
        // Pick hardcoded items from facets. Hardcode sorting.
        // Some items aren't sorted. These are returned and rendered as provided in the data.
        // Return object with sorted facets and the remaining unsorted facets.
        const {
            [FILTER_FACET.BODYTYPES]: bodyTypesKeyword,
            [FILTER_FACET.MAKE]: makeKeyword,
            [FILTER_FACET.MODEL]: modelKeyword,
            [FILTER_FACET.PURCHASE_OPTIONS]: purchaseOptions,
            'dealer.dealerAlternativeName.keyword': dealerDealerAlternativeNameKeyword,
            price,
            mileage,
            year,
            santanderPaymentPerMonth,
            leasingPricePrivateFinancial,
            leasingPricePrivateOperational,
            leasingPriceBusinessOperational,
            leasingPriceBusinessFinancial,
            ...unsorted
        } = unsortedFacets;

        const sortedOthers = unsortedFacets
            ? [
                  {
                      key: FILTER_FACET.PURCHASE_OPTIONS,
                      options: { ...purchaseOptions },
                  },
                  {
                      key: FILTER_FACET.BODYTYPES,
                      options: { ...bodyTypesKeyword },
                  },
                  {
                      key: FILTER_FACET.MAKE,
                      options: { ...makeKeyword },
                  },
                  ...(modelKeyword
                      ? [
                            {
                                key: FILTER_FACET.MODEL,
                                options: { ...modelKeyword },
                            },
                        ]
                      : []),
                  {
                      key: 'dealer.dealerAlternativeName.keyword',
                      options: { ...dealerDealerAlternativeNameKeyword },
                  },
                  {
                      key: 'price',
                      options: { ...price },
                  },
                  {
                      key: 'santanderPaymentPerMonth',
                      options: { ...santanderPaymentPerMonth },
                  },
                  {
                      key: 'mileage',
                      options: { ...mileage },
                  },
                  {
                      key: 'year',
                      options: { ...year },
                  },
                  {
                    key: 'leasingPricePrivateFinancial',
                    options: { ...leasingPricePrivateFinancial },
                  },
                  {
                    key: 'leasingPricePrivateOperational',
                    options: { ...leasingPricePrivateOperational },
                  },
                  {
                    key: 'leasingPriceBusinessOperational',
                    options: { ...leasingPriceBusinessOperational },
                  },
                  {
                    key: 'leasingPriceBusinessFinancial',
                    options: { ...leasingPriceBusinessFinancial },
                  },
              ]
            : [];

        const result = {
            sorted: sortedOthers,
            unsorted,
        };

        return result;
    }

    private getIsNewOrIsVanIfAnyOfThemIsPresent(isNew: IFilterFacet, isVan: IFilterFacet) {
        return (isNew && isNew.items?.length) || (isVan && isVan.items?.length)
            ? {
                isNew,
                isVan,
            }
            : undefined;
    }

    private setBooleanFilterToTrueIfExists(filter: IFilterFacet) {
        if (filter && filter.items) {
            filter.items = filter.items.filter((item) => item.key === 'true');
        }
    }

    private discardInitialParamsFromFilterdata(initialFilterParams: IParamsOfArrays, filterData: IFilterResponse) {
        Object.keys(initialFilterParams).forEach((key) => {
            delete filterData?.facets?.[key];
        });
    }
}
